import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Sparkles,
  Shield,
  Loader2,
  Video,
  MessageSquare,
  Copy,
  Check,
  Trash2,
  Calendar as CalendarIcon,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const CATEGORIES = ["Event", "Holiday", "Program", "Community", "Weather"];

const CATEGORY_STYLE = {
  Event: { color: "#D4AF37", bg: "rgba(212,175,55,0.14)", border: "rgba(212,175,55,0.55)" },
  Holiday: { color: "#F43F5E", bg: "rgba(244,63,94,0.14)", border: "rgba(244,63,94,0.55)" },
  Program: { color: "#60A5FA", bg: "rgba(96,165,250,0.14)", border: "rgba(96,165,250,0.55)" },
  Community: { color: "#34D399", bg: "rgba(52,211,153,0.14)", border: "rgba(52,211,153,0.55)" },
  Weather: { color: "#A78BFA", bg: "rgba(167,139,250,0.14)", border: "rgba(167,139,250,0.55)" },
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// -------------- date helpers --------------
const pad = (n) => String(n).padStart(2, "0");
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseIso = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

function monthGridDays(year, month) {
  // month is 0-indexed. Returns a 6-row * 7-col array of {date, inMonth}.
  const firstOfMonth = new Date(year, month, 1);
  const startWeekday = firstOfMonth.getDay(); // 0 = Sun
  const start = new Date(year, month, 1 - startWeekday);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({ date: d, inMonth: d.getMonth() === month });
  }
  return cells;
}

// Fallback list so the selector works even if the /niches endpoint is slow.
const DEFAULT_NICHES = [
  { key: "campground", label: "Campground / Family Rec", persona: "Bentley the Otter" },
  { key: "automotive", label: "Automotive · Shop / Mechanic", persona: "The Service Director" },
  { key: "recruit_mlm", label: "Recruit / MLM · Team Momentum", persona: "The Squad Leader" },
  { key: "training", label: "Training / Coaching", persona: "The Head Coach" },
  { key: "general", label: "General Business", persona: "The Owner" },
];

const NICHE_STORAGE_KEY = "waa_campaign_niche";

export default function RangerAcademyCalendar() {
  const today = new Date();
  const [cursor, setCursor] = useState(() => new Date(2026, 5, 1)); // June 2026 default (seeded events)
  const [events, setEvents] = useState([]);
  const [niches, setNiches] = useState(DEFAULT_NICHES);
  const [niche, setNiche] = useState(() => {
    try {
      return localStorage.getItem(NICHE_STORAGE_KEY) || "campground";
    } catch (_) {
      return "campground";
    }
  });
  const [loading, setLoading] = useState(true);
  const [savingDragId, setSavingDragId] = useState(null);
  const [creatingOn, setCreatingOn] = useState(null); // ISO date string
  const [draftingFor, setDraftingFor] = useState(null); // event object

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const eventsByDate = useMemo(() => {
    const m = {};
    for (const ev of events) {
      if (!m[ev.date]) m[ev.date] = [];
      m[ev.date].push(ev);
    }
    return m;
  }, [events]);

  const cells = useMemo(() => monthGridDays(year, month), [year, month]);
  const todayIso = isoDate(today);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leader/ranger/events");
      setEvents(data.events || []);
    } catch (e) {
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    // Non-critical — silently keep default niches on failure.
    (async () => {
      try {
        const { data } = await api.get("/leader/ranger/niches");
        if (Array.isArray(data?.niches) && data.niches.length) {
          setNiches(data.niches);
        }
      } catch (_) {
        /* keep DEFAULT_NICHES */
      }
    })();
  }, []);

  // Persist the leader's niche pick — this is a per-account preference.
  useEffect(() => {
    try {
      localStorage.setItem(NICHE_STORAGE_KEY, niche);
    } catch (_) {
      /* localStorage unavailable — ignore */
    }
  }, [niche]);

  const activeNiche = useMemo(
    () => niches.find((n) => n.key === niche) || niches[0],
    [niches, niche]
  );

  const moveEvent = async (evId, newDate) => {
    const original = events.find((e) => e.id === evId);
    if (!original || original.date === newDate) return;
    // Optimistic
    setEvents((prev) => prev.map((e) => (e.id === evId ? { ...e, date: newDate } : e)));
    setSavingDragId(evId);
    try {
      await api.patch(`/leader/ranger/events/${evId}`, { date: newDate });
      toast.success(`Moved "${original.title}" to ${newDate}`);
    } catch (e) {
      // Revert
      setEvents((prev) => prev.map((e) => (e.id === evId ? { ...e, date: original.date } : e)));
      toast.error("Move failed");
    } finally {
      setSavingDragId(null);
    }
  };

  const deleteEvent = async (evId) => {
    const snap = events;
    setEvents((prev) => prev.filter((e) => e.id !== evId));
    try {
      await api.delete(`/leader/ranger/events/${evId}`);
      toast.success("Event removed");
    } catch (e) {
      setEvents(snap);
      toast.error("Delete failed");
    }
  };

  const createEvent = async (payload) => {
    try {
      const { data } = await api.post("/leader/ranger/events", payload);
      setEvents((prev) => [...prev, data.event]);
      toast.success("Event added");
      setCreatingOn(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const gotoPrev = () => setCursor(new Date(year, month - 1, 1));
  const gotoNext = () => setCursor(new Date(year, month + 1, 1));
  const gotoToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));

  return (
    <div className="space-y-6" data-testid="ranger-academy-calendar">
      {/* Elite Ops banner + niche selector */}
      <div
        className="relative overflow-hidden rounded-sm border p-5 flex flex-col md:flex-row md:items-center gap-4"
        style={{
          borderColor: "rgba(212,175,55,0.55)",
          background:
            "linear-gradient(90deg, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.05) 55%, rgba(6,11,20,0.7) 100%)",
          boxShadow: "0 0 44px -18px rgba(212,175,55,0.7)",
        }}
        data-testid="ranger-elite-ops-banner"
      >
        <div
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(212,175,55,0.9), transparent)",
          }}
        />
        <div
          className="w-11 h-11 rounded-sm border flex items-center justify-center shrink-0"
          style={{
            borderColor: "rgba(212,175,55,0.6)",
            background: "rgba(212,175,55,0.14)",
          }}
        >
          <Shield size={20} className="text-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[9px] font-display uppercase tracking-widest border"
              style={{
                color: "#F5C842",
                background: "rgba(212,175,55,0.16)",
                borderColor: "rgba(212,175,55,0.6)",
              }}
              data-testid="ranger-elite-ops-badge"
            >
              <Sparkles size={9} /> Elite Ops Tier
            </span>
            <span className="text-[10px] uppercase tracking-[0.24em] text-slate-400">
              Alfred Campaign Architect
            </span>
          </div>
          <div className="font-display text-xl sm:text-2xl text-white mt-1.5 leading-tight">
            Concierge marketing ops — Alfred writes the campaign, you approve the vibe.
          </div>
          {activeNiche && (
            <div
              className="text-[11px] text-slate-400 mt-1 font-mono-aviation"
              data-testid="ranger-niche-persona"
            >
              Voice: <span className="text-gold">{activeNiche.persona}</span> · Niche:{" "}
              <span className="text-slate-200">{activeNiche.label}</span>
            </div>
          )}
        </div>

        {/* Niche selector */}
        <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0 min-w-[220px]">
          <label
            htmlFor="ranger-niche-select"
            className="text-[9px] uppercase tracking-[0.28em] text-slate-500"
          >
            Business Niche
          </label>
          <select
            id="ranger-niche-select"
            value={niche}
            onChange={(e) => setNiche(e.target.value)}
            data-testid="ranger-niche-select"
            className="bg-[#060B14] border border-white/15 rounded-sm px-3 py-2 text-white text-xs focus:outline-none focus:border-gold appearance-none pr-8"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23D4AF37' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 10px center",
            }}
          >
            {niches.map((n) => (
              <option key={n.key} value={n.key} className="bg-[#060B14]">
                {n.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setCreatingOn(todayIso)}
            className="btn-gold px-5 py-2 rounded-sm text-xs inline-flex items-center justify-center gap-2"
            data-testid="ranger-add-event-button"
          >
            <Plus size={13} /> Add Event
          </button>
        </div>
      </div>

      {/* Calendar header */}
      <div className="glass-strong rounded-sm">
        <div className="px-5 py-4 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-2">
            <CalendarIcon size={16} className="text-gold" />
            <div>
              <div className="hud-label">Ranger Academy · Season Calendar</div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                Drag events between days · click any day to add
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={gotoPrev}
              data-testid="ranger-prev-month"
              className="p-2 rounded-sm border border-white/10 hover:border-gold/50 text-slate-300 hover:text-gold transition-colors"
            >
              <ChevronLeft size={14} />
            </button>
            <div
              className="font-display text-lg text-white px-3 min-w-[168px] text-center"
              data-testid="ranger-month-label"
            >
              {MONTHS[month]} {year}
            </div>
            <button
              onClick={gotoNext}
              data-testid="ranger-next-month"
              className="p-2 rounded-sm border border-white/10 hover:border-gold/50 text-slate-300 hover:text-gold transition-colors"
            >
              <ChevronRight size={14} />
            </button>
            <button
              onClick={gotoToday}
              data-testid="ranger-today-btn"
              className="btn-ghost-gold px-3 py-2 rounded-sm text-[10px] uppercase tracking-wider ml-1"
            >
              Today
            </button>
          </div>
        </div>

        {/* Weekday header + Month grid — share scroll region so cells align */}
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Loading calendar...</div>
        ) : (
          <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
            <div className="min-w-[560px]">
              {/* Weekday header */}
              <div className="grid grid-cols-7 text-center text-[10px] uppercase tracking-[0.22em] text-slate-500 border-b border-white/5">
                {WEEKDAYS.map((w) => (
                  <div key={w} className="py-2">
                    {w}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 auto-rows-[112px]" data-testid="ranger-month-grid">
                {cells.map((cell, i) => {
                  const iso = isoDate(cell.date);
                  const dayEvents = eventsByDate[iso] || [];
                  const isToday = iso === todayIso;
                  return (
                    <DayCell
                      key={i}
                      iso={iso}
                      date={cell.date}
                      inMonth={cell.inMonth}
                      isToday={isToday}
                      events={dayEvents}
                      savingDragId={savingDragId}
                      onDrop={(evId) => moveEvent(evId, iso)}
                      onAdd={() => setCreatingOn(iso)}
                      onDraft={(ev) => setDraftingFor(ev)}
                      onDelete={deleteEvent}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {creatingOn && (
        <CreateEventDialog
          date={creatingOn}
          onClose={() => setCreatingOn(null)}
          onSubmit={createEvent}
        />
      )}

      {draftingFor && (
        <DraftMarketingDialog
          event={draftingFor}
          niche={niche}
          nicheLabel={activeNiche?.label}
          persona={activeNiche?.persona}
          onClose={() => setDraftingFor(null)}
        />
      )}
    </div>
  );
}

// ---------------- Day cell ----------------
function DayCell({ iso, date, inMonth, isToday, events, savingDragId, onDrop, onAdd, onDraft, onDelete }) {
  const [dragOver, setDragOver] = useState(false);
  return (
    <div
      className="border-r border-b border-white/5 relative group"
      style={{
        backgroundColor: dragOver ? "rgba(212,175,55,0.08)" : inMonth ? "transparent" : "rgba(255,255,255,0.015)",
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const evId = e.dataTransfer.getData("text/plain");
        if (evId) onDrop(evId);
      }}
      data-testid={`ranger-day-${iso}`}
    >
      <div className="flex items-center justify-between px-2 pt-1.5">
        <span
          className={`text-[11px] font-mono-aviation ${
            isToday
              ? "text-black bg-gold rounded-sm px-1.5"
              : inMonth
              ? "text-slate-300"
              : "text-slate-600"
          }`}
        >
          {date.getDate()}
        </span>
        <button
          type="button"
          onClick={onAdd}
          className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-gold transition-all"
          data-testid={`ranger-add-day-${iso}`}
          title="Add event"
        >
          <Plus size={11} />
        </button>
      </div>
      <div className="px-1.5 pb-1 pt-1 space-y-1 overflow-hidden">
        {events.map((ev) => (
          <EventPill
            key={ev.id}
            ev={ev}
            saving={savingDragId === ev.id}
            onDraft={() => onDraft(ev)}
            onDelete={() => onDelete(ev.id)}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------- Event pill ----------------
function EventPill({ ev, saving, onDraft, onDelete }) {
  const style = CATEGORY_STYLE[ev.category] || CATEGORY_STYLE.Event;
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", ev.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      className="rounded-sm border px-2 py-1 text-[10px] font-display cursor-grab active:cursor-grabbing flex items-center justify-between gap-1 group/pill"
      style={{
        color: style.color,
        borderColor: style.border,
        background: style.bg,
        opacity: saving ? 0.6 : 1,
      }}
      data-testid={`ranger-event-${ev.id}`}
    >
      <span className="truncate flex-1" title={ev.title}>
        {ev.title}
      </span>
      <div className="flex items-center gap-0.5 opacity-0 group-hover/pill:opacity-100 transition-opacity shrink-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDraft();
          }}
          data-testid={`ranger-draft-btn-${ev.id}`}
          className="p-0.5 hover:text-white"
          title="Draft marketing with Alfred"
        >
          <Sparkles size={10} />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(`Remove "${ev.title}"?`)) onDelete();
          }}
          data-testid={`ranger-delete-btn-${ev.id}`}
          className="p-0.5 hover:text-red-400"
          title="Remove event"
        >
          <Trash2 size={10} />
        </button>
      </div>
    </div>
  );
}

// ---------------- Create dialog ----------------
function CreateEventDialog({ date, onClose, onSubmit }) {
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [category, setCategory] = useState("Event");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Title required");
      return;
    }
    setSaving(true);
    await onSubmit({ title: title.trim(), date, details: details.trim(), category });
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A1122] border-white/10 text-white max-w-lg" data-testid="ranger-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-light text-2xl flex items-center gap-3">
            <CalendarIcon size={20} className="text-gold" />
            New Ranger Event
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Scheduling for <span className="text-gold font-mono-aviation">{date}</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="hud-label text-[10px] mb-1.5 block">Title</label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="ranger-create-title"
              placeholder="Christmas in July"
              className="w-full bg-[#060B14] border border-white/10 rounded-sm px-3 py-2 text-white text-sm focus:outline-none focus:border-gold"
            />
          </div>
          <div>
            <label className="hud-label text-[10px] mb-1.5 block">Category</label>
            <div className="flex flex-wrap gap-1.5" data-testid="ranger-create-category">
              {CATEGORIES.map((c) => {
                const s = CATEGORY_STYLE[c];
                const active = category === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    className="px-2.5 py-1 rounded-sm text-[10px] font-display uppercase tracking-wider border transition-colors"
                    style={
                      active
                        ? { color: s.color, background: s.bg, borderColor: s.border }
                        : { color: "#94A3B8", background: "transparent", borderColor: "rgba(255,255,255,0.08)" }
                    }
                    data-testid={`ranger-cat-${c.toLowerCase()}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="hud-label text-[10px] mb-1.5 block">Details (optional)</label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              data-testid="ranger-create-details"
              placeholder="Theme, hooks, guest tie-ins..."
              className="w-full bg-[#060B14] border border-white/10 rounded-sm px-3 py-2 text-white text-sm focus:outline-none focus:border-gold resize-none"
            />
          </div>
          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={saving}
              data-testid="ranger-create-submit"
              className="btn-gold flex-1 py-3 rounded-sm text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {saving ? "Saving..." : "Add to Calendar"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-3 rounded-sm text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"
            >
              <X size={12} /> Cancel
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Draft Marketing dialog ----------------
function DraftMarketingDialog({ event, niche, nicheLabel, persona, onClose }) {
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState(null);
  const [model, setModel] = useState("");
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/leader/ranger/draft-marketing", {
          event_id: event.id,
          title: event.title,
          date: event.date,
          details: event.details,
          category: event.category,
          niche,
        });
        if (cancelled) return;
        setDrafts(data.drafts);
        setModel(data.model || "");
      } catch (e) {
        if (!cancelled) toast.error("Alfred could not draft this — try again shortly.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [event.id, niche]);

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1400);
      toast.success("Copied");
    } catch (_) {
      toast.error("Clipboard blocked");
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#0A1122] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="ranger-draft-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-light text-2xl flex items-center gap-3">
            <Sparkles size={20} className="text-gold" />
            Alfred · Campaign Draft
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs" data-testid="ranger-draft-meta">
            {event.title} · <span className="font-mono-aviation">{event.date}</span>
            {(nicheLabel || persona) && (
              <span className="ml-2 text-[10px] uppercase tracking-[0.22em] text-slate-500">
                · {persona || "Owner"}
                {nicheLabel ? ` · ${nicheLabel}` : ""}
              </span>
            )}
            {model && (
              <span className="ml-2 text-[10px] uppercase tracking-[0.22em] text-slate-500">
                · via {model}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-slate-400 text-sm">
            <Loader2 size={20} className="animate-spin mx-auto mb-3 text-gold" />
            Alfred is drafting the {persona ? `${persona} ` : ""}script and social caption...
          </div>
        ) : drafts ? (
          <div className="space-y-5">
            <DraftBlock
              title={persona ? `${persona} · Video Script` : "Video Script"}
              icon={<Video size={14} />}
              testId="ranger-draft-video"
              body={drafts.video_script}
              onCopy={() => copy("video", drafts.video_script)}
              copied={copiedKey === "video"}
            />
            <DraftBlock
              title="Social Media Caption"
              icon={<MessageSquare size={14} />}
              testId="ranger-draft-social"
              body={drafts.social_caption}
              onCopy={() => copy("social", drafts.social_caption)}
              copied={copiedKey === "social"}
            />
          </div>
        ) : (
          <div className="py-8 text-center text-slate-500 text-sm">No draft returned.</div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DraftBlock({ title, icon, body, onCopy, copied, testId }) {
  return (
    <div
      className="rounded-sm border p-4"
      style={{
        borderColor: "rgba(212,175,55,0.35)",
        background: "rgba(6,11,20,0.6)",
      }}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-gold">{icon}</span>
          <div className="font-display text-sm text-white">{title}</div>
        </div>
        <button
          type="button"
          onClick={onCopy}
          data-testid={`${testId}-copy`}
          className="btn-ghost-gold px-2.5 py-1 rounded-sm text-[10px] inline-flex items-center gap-1"
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap text-[12px] text-slate-200 font-mono-aviation leading-relaxed">
        {body || "(empty)"}
      </pre>
    </div>
  );
}
